import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, RefreshCw, Users } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { AgendaPill } from "@/components/StatusPill";
import { displayDate, shiftOpenDay, shortDate, skipSunday, toApiDate, todayIso, weekdayShort } from "@/lib/fmt";

interface Participant {
  idCliente: number;
  nome: string;
  foto: string | null;
  /** Member opted out of participation lists: show anonymised. */
  oculto: boolean;
}

interface ClassCheckins {
  idConfiguracao: number;
  nome: string;
  horaInicio: string;
  horaFim: string | null;
  professor: string | null;
  status: number;
  ocupacao: number;
  capacidade: number;
  participants: Participant[];
}

interface DayCheckins {
  date: string;
  total: number;
  classes: ClassCheckins[];
}

/** Small round avatar; opted-out members get a neutral placeholder. */
function PersonAvatar({ person }: { person: Participant }) {
  const [broken, setBroken] = useState(false);
  const showPhoto = !person.oculto && person.foto && !broken;
  return (
    <span
      style={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        overflow: "hidden",
        display: "grid",
        placeItems: "center",
        background: "var(--cb-border2)",
        flex: "none",
      }}
    >
      {showPhoto ? (
        <img
          src={person.foto ?? ""}
          alt=""
          width={22}
          height={22}
          loading="lazy"
          style={{ objectFit: "cover" }}
          onError={() => setBroken(true)}
        />
      ) : (
        <Users size={12} style={{ color: "var(--cb-subtle)" }} />
      )}
    </span>
  );
}

function Occupancy({ ocupacao, capacidade }: { ocupacao: number; capacidade: number }) {
  if (!capacidade) return null;
  const pct = Math.min((ocupacao / capacidade) * 100, 100);
  const color = pct >= 100 ? "var(--cb-error)" : pct >= 70 ? "var(--cb-warning)" : "var(--cb-success)";
  return (
    <div style={{ display: "grid", gap: "0.35rem", maxWidth: "12rem" }}>
      <div className="cb-meter">
        <div className="cb-meter__fill" style={{ background: color, width: `${pct}%` }} />
      </div>
      <span className="cb-label" style={{ letterSpacing: "0.14em" }}>
        {ocupacao}/{capacidade} lugares
      </span>
    </div>
  );
}

/** Day lives in the URL (`#aulas?dia=YYYY-MM-DD`) so reload, back and share keep the view. */
function dayFromHash(): string | null {
  const match = /[?&]dia=(\d{4}-\d{2}-\d{2})/.exec(window.location.hash);
  return match?.[1] ?? null;
}

export function CheckinsTab() {
  const [date, setDate] = useState(() => dayFromHash() ?? skipSunday(todayIso()));
  const [day, setDay] = useState<DayCheckins | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  /** Real name behind an "anônimo" roster entry, resolved automatically per id. */
  const [revealed, setRevealed] = useState<Record<number, string>>({});

  const anonymousIds = useMemo(() => {
    const ids = new Set<number>();
    for (const cls of day?.classes ?? []) {
      for (const p of cls.participants) if (p.oculto) ids.add(p.idCliente);
    }
    return [...ids];
  }, [day]);

  useEffect(() => {
    const pending = anonymousIds.filter((id) => revealed[id] === undefined).slice(0, 20);
    if (pending.length === 0) return;
    let alive = true;
    void (async () => {
      for (const id of pending) {
        try {
          const res = await fetch(`/api/staff/students/${id}/identity`, { cache: "no-store" });
          if (!res.ok) continue;
          const body = (await res.json()) as { nomeCompleto?: string | null };
          if (!alive) return;
          if (body.nomeCompleto) setRevealed((prev) => ({ ...prev, [id]: body.nomeCompleto as string }));
        } catch {
          // sem revelação: a lista segue mostrando "anônimo"
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [anonymousIds, revealed]);

  useEffect(() => {
    const target = `aulas?dia=${date}`;
    if (window.location.hash.replace(/^#/, "") !== target) {
      window.history.replaceState(null, "", `#${target}`);
    }
  }, [date]);

  useEffect(() => {
    const onHash = () => {
      const fromHash = dayFromHash();
      if (fromHash) setDate((current) => (fromHash === current ? current : fromHash));
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const load = useCallback(async (iso: string) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/api/agenda/${toApiDate(iso)}/participants`, { cache: "no-store" });
      const body = (await res.json().catch(() => null)) as DayCheckins | { error?: string } | null;
      if (!res.ok) {
        setDay(null);
        setError((body && "error" in body && body.error) || `falha ao carregar (HTTP ${res.status})`);
        return;
      }
      setDay(body as DayCheckins);
    } catch (e) {
      setDay(null);
      setError(e instanceof Error ? e.message : "falha de rede");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(date);
  }, [date, load]);

  const isToday = date === todayIso();
  const classes = day?.classes ?? [];
  const full = classes.filter((c) => c.capacidade > 0 && c.ocupacao >= c.capacidade).length;

  return (
    <div style={{ display: "grid", gap: "0.85rem" }}>
      {/* ── day toolbar ─────────────────────────────────────── */}
      <section className="cb-panel cb-rise">
        <div className="cb-panel__body" style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <div className="cb-row" style={{ gap: "0.35rem" }}>
            <button
              type="button"
              className="cb-btn cb-btn--ghost cb-btn--sm"
              onClick={() => setDate(shiftOpenDay(date, -1))}
              aria-label="Dia anterior"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              className={isToday ? "cb-btn cb-btn--sm" : "cb-btn cb-btn--sm cb-btn--primary"}
              onClick={() => setDate(skipSunday(todayIso()))}
            >
              Hoje
            </button>
            <button
              type="button"
              className="cb-btn cb-btn--ghost cb-btn--sm"
              onClick={() => setDate(shiftOpenDay(date, 1))}
              aria-label="Próximo dia"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          <div style={{ minWidth: 0 }}>
            <p className="cb-display" style={{ fontSize: "1.6rem", margin: 0 }}>
              {weekdayShort(date)}{" "}
              <span style={{ color: isToday ? "var(--cb-accent)" : "var(--cb-text)" }}>{shortDate(date)}</span>
            </p>
            <p className="cb-label" style={{ marginTop: "0.1rem" }}>
              {displayDate(date)}
            </p>
          </div>

          <div className="cb-spacer" />

          <DatePicker value={date} onChange={(v) => setDate(skipSunday(v))} className="w-[11rem]" id="cb-checkins-date" />

          <button
            type="button"
            className="cb-btn cb-btn--ghost cb-btn--sm"
            onClick={() => void load(date)}
            aria-label="Recarregar"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : undefined} />
          </button>
        </div>

        {(error || day) && (
          <div
            className="cb-fade"
            style={{
              padding: "0.5rem 1.1rem",
              borderTop: "1px solid var(--cb-border)",
              fontFamily: "var(--font-mono)",
              fontSize: "0.72rem",
              color: error ? "var(--cb-error)" : "var(--cb-accent)",
            }}
          >
            {error
              ? error
              : `${classes.length} aulas com gente · ${day?.total ?? 0} check-ins${full > 0 ? ` · ${full} lotada${full > 1 ? "s" : ""}` : ""}`}
          </div>
        )}
      </section>

      {/* ── check-ins ───────────────────────────────────────── */}
      <section className="cb-panel cb-rise-1">
        <div className="cb-panel__head">
          <span className="cb-panel__title">
            <Users size={13} />
            Check-ins do dia
          </span>
          <span className="cb-label">{day?.total ?? 0} pessoas</span>
        </div>

        {loading && classes.length === 0 ? (
          <div className="cb-empty">consultando as aulas…</div>
        ) : classes.length === 0 ? (
          <div className="cb-empty">
            <strong>Ninguém agendado neste dia</strong>
            <span>as aulas aparecem aqui conforme as reservas entram</span>
          </div>
        ) : (
          classes.map((item) => {
            return (
              <div
                key={item.idConfiguracao}
                style={{ padding: "0.85rem 1rem", borderBottom: "1px solid var(--cb-border)" }}
              >
                <div className="cb-row" style={{ gap: "0.75rem", alignItems: "baseline", flexWrap: "wrap" }}>
                  <span className="cb-class__time">{item.horaInicio}</span>
                  <span style={{ fontWeight: 500 }}>{item.nome}</span>
                  <AgendaPill status={item.status} ocupacao={item.ocupacao} capacidade={item.capacidade} />
                  <span className="cb-label">
                    {item.horaInicio}
                    {item.horaFim ? `–${item.horaFim}` : ""}
                  </span>
                  {item.professor && <span className="cb-label">prof. {item.professor}</span>}
                  <div className="cb-spacer" />
                  <Occupancy ocupacao={item.ocupacao} capacidade={item.capacidade} />
                </div>

                {item.participants.length > 0 && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(18rem, 1fr))",
                      gap: "0.35rem 1.25rem",
                      marginTop: "0.75rem",
                    }}
                  >
                    {item.participants.map((p) => (
                      <div key={p.idCliente} className="cb-row" style={{ gap: "0.5rem", alignItems: "center", minWidth: 0 }}>
                        <PersonAvatar person={p} />
                        <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                          {revealed[p.idCliente] ?? (p.oculto ? "anônimo" : p.nome)}
                        </span>
                        {p.oculto && (
                          <span
                            className="cb-chip"
                            style={{
                              color: "var(--cb-warning)",
                              background: "var(--cb-warning-dim)",
                              borderColor: "rgba(255,182,46,.35)",
                              fontSize: "0.62rem",
                            }}
                          >
                            anônimo
                          </span>
                        )}
                        <button
                          type="button"
                          className="cb-btn cb-btn--ghost cb-btn--sm"
                          onClick={() => {
                            window.location.hash = `aluno/${p.idCliente}${p.oculto ? "/anon" : ""}`;
                          }}
                        >
                          Perfil
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </section>

      <p className="cb-label" style={{ textAlign: "center", color: "var(--cb-subtle)" }}>
        <CalendarDays size={11} style={{ verticalAlign: "-1px" }} /> o botão <strong>Perfil</strong> abre a página do
        aluno com tudo que o app de staff devolve para aquele id
      </p>
    </div>
  );
}
